import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface InvoiceData {
  invoice_number: string;
  created_at: string;
  due_date?: string;
  status: string;
  clients: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    wilaya?: string;
    tax_id?: string;
    nrc?: string;
    nis?: string;
    article_number?: string;
  };
  invoice_items: Array<{
    products: { name: string; code?: string };
    quantity: number;
    unit: string;
    unit_price: number;
    vat: number;
    discount?: number;
    discount_type?: string;
    total: number;
    description?: string;
  }>;
  subtotal: number;
  tax_amount: number;
  discount?: number;
  stamp_duty?: number;
  total: number;
  paid_amount?: number;
  notes?: string;
  payment_terms?: string;
}

/**
 * Format number for Algerian invoices
 * Uses spaces as thousand separators and comma for decimals
 * Example: 259000.00 → "259 000,00"
 */
function formatAlgerianNumber(value: number): string {
  return new Intl.NumberFormat('fr-DZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Reverse Arabic text for proper PDF rendering
 * jsPDF doesn't support RTL natively, so we need to reverse the text
 */
function reverseArabicText(text: string): string {
  // Split into words and reverse
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  
  if (!arabicPattern.test(text)) {
    return text; // No Arabic characters, return as is
  }
  
  // Simple reversal for Arabic text
  // For production, consider using a library like 'rtl-detect' and 'arabic-reshaper'
  return text.split('').reverse().join('');
}

export async function generateInvoicePDF(invoice: InvoiceData) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  let yPos = 20;

  // ========== HEADER ==========
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;

  // Company Info (Left) & Invoice Info (Right)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Left side - Company info (placeholder - should come from company settings)
  doc.text('Votre Entreprise', 20, yPos);
  doc.text('Adresse de l\'entreprise', 20, yPos + 5);
  doc.text('Tél: 0XX XX XX XX XX', 20, yPos + 10);
  doc.text('NIF: XXXXXXXXXXXXXX', 20, yPos + 15);
  
  // Right side - Invoice info
  doc.setFont('helvetica', 'bold');
  doc.text(`N° ${invoice.invoice_number}`, pageWidth - 20, yPos, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageWidth - 20, yPos + 5, { align: 'right' });
  if (invoice.due_date) {
    doc.text(`Échéance: ${new Date(invoice.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageWidth - 20, yPos + 10, { align: 'right' });
  }
  
  yPos += 30;

  // ========== CLIENT INFO ==========
  doc.setFillColor(240, 240, 240);
  doc.rect(20, yPos, pageWidth - 40, 25, 'F');
  
  yPos += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT', 25, yPos);
  
  yPos += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.clients.name, 25, yPos);
  
  if (invoice.clients.address) {
    yPos += 5;
    doc.text(invoice.clients.address, 25, yPos);
  }
  
  if (invoice.clients.phone || invoice.clients.email) {
    yPos += 5;
    const contactInfo = [];
    if (invoice.clients.phone) contactInfo.push(`Tél: ${invoice.clients.phone}`);
    if (invoice.clients.email) contactInfo.push(invoice.clients.email);
    doc.text(contactInfo.join(' | '), 25, yPos);
  }
  
  if (invoice.clients.tax_id || invoice.clients.nrc) {
    yPos += 5;
    const fiscalInfo = [];
    if (invoice.clients.tax_id) fiscalInfo.push(`NIF: ${invoice.clients.tax_id}`);
    if (invoice.clients.nrc) fiscalInfo.push(`RC: ${invoice.clients.nrc}`);
    doc.text(fiscalInfo.join(' | '), 25, yPos);
  }
  
  yPos += 15;

  // ========== ITEMS TABLE ==========
  const tableData = invoice.invoice_items.map(item => {
    const row = [
      item.products.code || '',
      item.products.name + (item.description ? `\n${item.description}` : ''),
      `${item.quantity} ${item.unit}`,
      formatAlgerianNumber(item.unit_price),
      `${item.vat}%`,
    ];
    
    if (invoice.invoice_items.some(i => i.discount && i.discount > 0)) {
      row.push(
        item.discount && item.discount > 0
          ? item.discount_type === 'percentage'
            ? `${item.discount}%`
            : formatAlgerianNumber(item.discount)
          : '-'
      );
    }
    
    row.push(`${formatAlgerianNumber(item.total)} DA`);
    
    return row;
  });

  const headers = [
    'Code',
    'Designation',
    'Qte',
    'P.U. HT',
    'TVA',
  ];
  
  if (invoice.invoice_items.some(i => i.discount && i.discount > 0)) {
    headers.push('Remise');
  }
  
  headers.push('Total TTC');

  autoTable(doc, {
    startY: yPos,
    head: [headers],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 15, halign: 'center' },
      5: { cellWidth: 30, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // ========== NOTES ==========
  if (invoice.notes) {
    doc.setFillColor(255, 252, 240);
    const notesHeight = 15;
    doc.rect(20, yPos, (pageWidth - 40) * 0.6, notesHeight, 'F');
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 25, yPos + 5);
    doc.setFont('helvetica', 'normal');
    
    const splitNotes = doc.splitTextToSize(invoice.notes, (pageWidth - 50) * 0.6);
    doc.text(splitNotes, 25, yPos + 9);
    
    yPos += notesHeight + 5;
  }

  // ========== TOTALS (Right side) ==========
  const totalsX = pageWidth - 80;
  let totalsY = yPos - (invoice.notes ? 15 : 0);
  
  doc.setFontSize(10);
  
  // Subtotal
  doc.setFont('helvetica', 'normal');
  doc.text('Sous-total HT:', totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.subtotal || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  totalsY += 6;
  
  // Discount
  if (invoice.discount && invoice.discount > 0) {
    doc.setTextColor(220, 38, 38);
    doc.text('Remise:', totalsX, totalsY);
    doc.text(`-${formatAlgerianNumber(invoice.discount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    totalsY += 6;
  }
  
  // TVA
  doc.text('TVA:', totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.tax_amount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  totalsY += 6;
  
  // Stamp duty
  if (invoice.stamp_duty && invoice.stamp_duty > 0) {
    doc.text('Timbre fiscal:', totalsX, totalsY);
    doc.text(`${formatAlgerianNumber(invoice.stamp_duty || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    totalsY += 6;
  }
  
  // Total line
  doc.setLineWidth(0.5);
  doc.line(totalsX, totalsY, pageWidth - 20, totalsY);
  totalsY += 7;
  
  // Total TTC
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129);
  doc.text('Total TTC:', totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.total || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  
  // Paid amount & Remaining
  if (invoice.paid_amount && invoice.paid_amount > 0) {
    totalsY += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(34, 197, 94);
    doc.text('Payé:', totalsX, totalsY);
    doc.text(`${formatAlgerianNumber(invoice.paid_amount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    
    const remaining = (invoice.total || 0) - (invoice.paid_amount || 0);
    if (remaining > 0) {
      totalsY += 6;
      doc.setTextColor(239, 68, 68);
      doc.text('Reste à payer:', totalsX, totalsY);
      doc.text(`${formatAlgerianNumber(remaining)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }

  // ========== PAYMENT TERMS ==========
  if (invoice.payment_terms) {
    yPos = Math.max(yPos + 30, totalsY + 15);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`Conditions de paiement: ${invoice.payment_terms}`, 20, yPos);
  }

  // ========== FOOTER ==========
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(128, 128, 128);
  doc.text('Merci pour votre confiance', pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text(`Facture générée le ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

  // ========== SAVE ==========
  doc.save(`Facture-${invoice.invoice_number}.pdf`);
}
